import React, { useEffect, useState } from 'react';

import styled from 'styled-components';
import { object, number } from 'yup';
import TemplateSelector from '../components/TemplateSelector';
import Select from 'container/components/Select/Select';
import { useSelector, useDispatch } from 'react-redux';
import RangeInput from '../components/RangeInput';
import {
  imageDimensions,
  imageGenerationModels,
} from '../../../../../../app.config';
import TemplateSelection from './TemplateSelection/TemplateSelection';
import assets from 'assets';
import useMediaQuery from 'container/hooks/useMediaQuery';
import Checkbox from 'container/components/Checkbox/Checkbox';
import theme from 'container/theme';
import { validate } from 'container/utils/validations';
import Switch from 'container/components/Switch/Switch';

const schema = object({
  height: number().min(90).max(2800).required(),
  width: number().min(90).max(2800).required(),
});

const Controls = ({ sideBar }) => {
  const dispatch = useDispatch();

  const mdScreen = useMediaQuery('(max-width: 1000px)');

  const [panelWidth, setPanelWidth] = useState(0);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [openImageSelector, setOpenImageSelector] = useState(false);
  const [dimensionsError, setDimensionsError] = useState('');
  const [dSelectorValue, setDSelectorValue] = useState('');
  const [customDimension, setCustomDimension] = useState(false);

  const [showExclude, setShowExclue] = useState(false);

  const {
    width,
    height,
    model,
    prompt_strength,
    neg_prompt,
    seed,
    keepSeed,
    grid,
    loading,
  } = useSelector(state => state.imageGeneration);

  const toggleAdvanced = () => {
    setShowAdvanced(!showAdvanced);
  };

  const handleNegativePrompt = e => {
    dispatch.imageGeneration.setNegPrompt(e.currentTarget.value);
  };

  const handleChangeModel = e => {
    dispatch.imageGeneration.setModel(e.currentTarget.value);
  };

  // const handleDimension = (width, height) => {
  //   dispatch.imageGeneration.setHeight(height);
  //   dispatch.imageGeneration.setWidth(width);
  // };

  const open = useSelector(state => state.utils.openControls);
  const {
    imageCount,
    filters,
    selectedFilter,
    selectedTemplate,
    templates,
    promptEdited,
  } = useSelector(state => state.imageGeneration);

  const handleSetWidth = () => {
    if (sideBar.current) {
      setPanelWidth(
        sideBar.current.offsetWidth
          ? sideBar.current.offsetWidth + 'px'
          : '100%'
      );
    }
  };

  const handlePromptStrength = e => {
    dispatch.imageGeneration.setPromptStrength(+e.currentTarget.value);
  };

  const handleDimensions = e => {
    const { name, value } = e.currentTarget;
    if (name === 'width') {
      dispatch.imageGeneration.setWidth(+value);
    } else {
      dispatch.imageGeneration.setHeight(+value);
    }
  };

  const handleOnSelect = name => {
    const filter = filters.find(f => f.name === name);
    console.log(filter);
    dispatch.imageGeneration.setSelectedFilter(filter);
  };

  const handleImageCount = count => {
    dispatch.imageGeneration.setImageCount(count);
  };

  const handleTemplateSelect = val => {
    dispatch.imageGeneration.setSelectedTemplate(val);
    dispatch.imageGeneration.setPromptEdited(false);
    setOpenImageSelector(true);
  };

  const validateDimensions = async () => {
    const errors = await validate(schema, { height, width });
    if (errors) {
      const errorMessages = Object?.keys(errors).map(msg => errors[msg]);
      setDimensionsError(errorMessages.join('. '));
      dispatch.imageGeneration.setDisable(true);
    } else {
      setDimensionsError('');
      dispatch.imageGeneration.setDisable(false);
    }
  };

  const handleDimensionChange = e => {
    const value = e.target.value;
    if (value === 'custom') {
      setDSelectorValue(value);
      return setCustomDimension(true);
    } else {
      setCustomDimension(false);
    }
    const dimensions = value?.split(' x ');
    dispatch.imageGeneration.setWidth(+dimensions[0]);
    dispatch.imageGeneration.setHeight(+dimensions[1]);
    setDSelectorValue(value);
  };

  useEffect(() => {
    handleSetWidth();
    window.addEventListener('resize', handleSetWidth);
    return () => window.removeEventListener('resize', handleSetWidth);
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (openImageSelector && promptEdited) {
      setOpenImageSelector(false);
    }
    // eslint-disable-next-line
  }, [promptEdited, openImageSelector]);

  useEffect(() => {
    if (width === 0) {
      dispatch.imageGeneration.setWidth('');
    }
    if (height === 0) {
      dispatch.imageGeneration.setHeight('');
    }

    validateDimensions();
    // eslint-disable-next-line
  }, [width, height]);

  useEffect(() => {
    setDSelectorValue(imageDimensions[1].value);
    const val = imageDimensions[1].value;
    const dimensions = val?.split(' x ');
    dispatch.imageGeneration.setWidth(+dimensions[0]);
    dispatch.imageGeneration.setHeight(+dimensions[1]);
    // eslint-disable-next-line
  }, []);

  return (
    <Container
      style={{
        width: panelWidth,
        transform: `translateX(${
          open ? (mdScreen ? '0%' : '100%') : mdScreen ? '100%' : '0%'
        })`,
      }}
    >
      <div className='content'>
        <div
          className='close-btn'
          style={{
            position: 'absolute',
            right: '10px',
            top: '10px',
            display: mdScreen ? 'block' : 'none',
            cursor: 'pointer',
          }}
        >
          <img
            src={assets.closeIcon}
            onClick={() => dispatch.utils.setOpenControls(false)}
            alt=''
          />
        </div>
        <div id='step-2'>
          <p className='font-size-16'>Prompt Details</p>
          <p className='subtitle font-size-10'>
            Choose to create a custom image using a text prompt or select a
            pre-made image template to begin.
          </p>
          <div
            className='temp-selector'
            // role='button' onClick={handleClick}
          >
            <TemplateSelector
              containerStyles={{
                backgroundColor: theme.colors.darkgray,
              }}
              onSelect={handleTemplateSelect}
              options={Object.keys(templates).map(template => ({
                img: templates[template || 'Art'][0].example_image_url,
                label: template,
              }))}
              selected={{
                img: promptEdited
                  ? assets.custom
                  : selectedTemplate
                  ? selectedTemplate === 'custom'
                    ? assets.custom
                    : templates[selectedTemplate || 'Art'][0].example_image_url
                  : assets.custom,
                label: promptEdited
                  ? 'Custom prompt'
                  : selectedTemplate || 'Select an option',
              }}
            />
            {/* <SelectedTemplate>
              <div className='inner'>
                {promptEdited ? (
                  <img src={assets.custom} className='image' alt='' />
                ) : (
                  <img
                    src={
                      templates && templates[selectedTemplate]
                        ? templates[selectedTemplate][0]?.example_image_url
                        : assets.preview
                    }
                    className='image'
                    alt=''
                  />
                )}

                <p className='font-size-12'>
                  {promptEdited
                    ? 'Custom prompt'
                    : selectedTemplate || 'Select'}
                </p>
              </div>
            </SelectedTemplate> */}
          </div>
          <Divider />
        </div>
        <div id='step-3'>
          <p className='font-size-16'>Filter</p>
          <p className='subtitle font-size-10'>
            Experiment with different styles that can be applied to your image.
          </p>
          <div className='temp-selector'>
            <TemplateSelector
              containerStyles={{
                backgroundColor: theme.colors.darkgray,
              }}
              selected={{
                img: selectedFilter?.image_url || assets.preview,
                label: selectedFilter?.name || 'Select',
              }}
              onSelect={handleOnSelect}
              options={
                filters.length
                  ? filters.map(filter => ({
                      img: filter.image_url,
                      label: filter.name,
                    }))
                  : []
              }
            />
          </div>
          <Divider />
        </div>
        <div id='step-4'>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <p className='font-size-16'>Exclude from image</p>
            <Switch
              checked={showExclude}
              onChange={e => setShowExclue(e.target.checked)}
            />
          </div>

          {showExclude && (
            <>
              <p className='subtitle font-size-10'>
                Describe details you don’t want in your image like color,
                objects or a scenery.
              </p>
              <TextArea
                placeholder='goldfish, pink, blurry'
                value={neg_prompt}
                onChange={handleNegativePrompt}
                rows={5}
              ></TextArea>
            </>
          )}
          <Divider />
        </div>
        <div id='step-5'>
          <p className='font-size-16'>Image Dimensions</p>
          <p className='subtitle font-size-10'>Width x Height of the image.</p>

          <div style={{ marginBlock: '20px' }}>
            <Select
              value={dSelectorValue}
              onChange={handleDimensionChange}
              options={imageDimensions}
            />
          </div>

          {customDimension && (
            <>
              <p className='subtitle font-size-10'>Custom dimensions</p>
              <CustomDimensions>
                <Input
                  placeholder='Add width'
                  type='number'
                  value={width}
                  name='width'
                  onChange={handleDimensions}
                  min={90}
                  max={1024}
                  className={dimensionsError ? 'error' : ''}
                />
                <Input
                  placeholder='Add height'
                  type='number'
                  name='height'
                  value={height}
                  onChange={handleDimensions}
                  min={90}
                  max={1024}
                  className={dimensionsError ? 'error' : ''}
                />
              </CustomDimensions>
            </>
          )}
          {dimensionsError && (
            <p className='font-size-12 error-message'>
              Dimensions must be between 90 and 2800
            </p>
          )}

          <Divider />
        </div>
        <div id='step-6'>
          <p className='font-size-16'>Number of images</p>
          <p className='subtitle font-size-10'>
            Select the number of images you would like to generate.
          </p>
          <ImageCountGrid>
            <Button
              className={imageCount === 1 ? 'active' : ''}
              onClick={() => handleImageCount(1)}
              disabled={loading}
            >
              1
            </Button>
            <Button
              className={imageCount === 2 ? 'active' : ''}
              onClick={() => handleImageCount(2)}
              disabled={loading}
            >
              2
            </Button>
            {/* <Button
              className={imageCount === 3 ? 'active' : ''}
              onClick={() => handleImageCount(3)}
              disabled={loading}
            >
              3
            </Button>
            <Button
              className={imageCount === 4 ? 'active' : ''}
              onClick={() => handleImageCount(4)}
              disabled={loading}
            >
              4
            </Button> */}
          </ImageCountGrid>
          <Divider />
        </div>
        <p className='font-size-16'>Mode</p>
        <p className='subtitle font-size-10'>
          Advanced mode show rarely used parameters.
        </p>
        <DimensionButtons>
          <Button
            className={showAdvanced ? '' : 'active'}
            onClick={toggleAdvanced}
          >
            Basic
          </Button>
          <Button
            className={showAdvanced ? 'active' : ''}
            onClick={toggleAdvanced}
          >
            Advanced
          </Button>
        </DimensionButtons>
        {showAdvanced && (
          <>
            <Divider />
            <p className='font-size-16'>Columns</p>
            <p className='subtitle font-size-10'>
              Choose number of columns to display in viewer.
            </p>
            <RangeInput
              value={grid}
              min={1}
              max={3}
              onChange={e => {
                dispatch.imageGeneration.setGrid(e.target.value);
              }}
            />
            <Divider />
            <p className='font-size-16'>Model</p>
            <p className='subtitle font-size-10'>
              Different AI models can produce different or better results so
              feel free to experiment.
            </p>
            <div className='select-container'>
              <Select
                options={imageGenerationModels}
                onChange={handleChangeModel}
                value={model}
              />
            </div>
            <Divider />
            <p className='font-size-16'>Prompt Guidance</p>
            <p className='subtitle font-size-10'>
              Higher values will make your image closer to your prompt.
            </p>
            <div className='select-container'>
              <RangeInput
                min={7}
                max={30}
                value={prompt_strength}
                onChange={handlePromptStrength}
              />
            </div>
            {/* <Divider />
            <p className='font-size-16'>Quality & Details</p>
            <p className='subtitle font-size-10'>
              More steps will result in a high quality image but will take
              longer.
            </p>
            <div className='select-container'>
              <RangeInput value={7} onChange={() => {}} />
            </div> */}
            <Divider />
            <p className='font-size-16'>Seed</p>
            <p className='subtitle font-size-10'>
              Different numbers result in new variations of your image.
            </p>

            <Input
              value={seed}
              onChange={e => dispatch.imageGeneration.setSeed(e.target.value)}
            />

            <div className='flex-row'>
              <Checkbox
                checked={keepSeed}
                onChange={() => dispatch.imageGeneration.setKeepSeed(!keepSeed)}
              />
              <p className='font-size-10'>Use same seed for the next prompt.</p>
            </div>
          </>
        )}

        <Divider />

        {openImageSelector && (
          <TemplateSelection setOpen={setOpenImageSelector} />
        )}
      </div>
    </Container>
  );
};

export default Controls;

const Container = styled.div`
  position: absolute;
  top: 0px;
  right: 0px;
  display: flex;
  flex-direction: column;
  /* height: calc(100% - 230px); */
  height: 100%;
  padding-bottom: 7px;
  z-index: 10;
  transition: 0.4s ease;
  border-radius: 0px 12px 12px 0px;
  overflow: hidden;

  & .error-message {
    margin-top: 10px !important;
  }

  & .flex-row {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-top: 10px;
  }

  & .content {
    position: relative;
    flex-grow: 1;
    height: 0px;
    overflow: auto;
    // background-color: ${({ theme }) => theme.colors.darkgrey};
    background-color: #39393b;
    border-radius: 12px;
    border-left: 10px solid ${({ theme }) => theme.colors.darkbg};
    padding: 10px;
    color: ${({ theme }) => theme.colors.primary};
    &::-webkit-scrollbar-track {
      background-color: ${({ theme }) => theme.colors.darkgrey};
    }
    & p {
      margin: 0px;

      &.subtitle {
        opacity: 0.5;
        z-index: 0;
      }
    }

    & .temp-selector {
      margin-top: 10px;
    }

    & .select-container {
      margin-block: 10px 12px;
    }
  }

  @media only screen and (max-width: 1000px) {
    z-index: 30;
    border-radius: 0px;
    width: 350px !important;
    & .content {
      padding-top: 30px;
    }
  }
  @media only screen and (max-width: 420px) {
    z-index: 30;
    border-radius: 0px;
    width: 100% !important;
  }
`;

const Divider = styled.div`
  border-top: 1px solid ${({ theme }) => theme.colors.lightgrey};
  margin-block: 18px;
`;

const DimensionButtons = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 6px;
  margin-block: 10px 14px;
`;

const ImageCountGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 6px;
  margin-top: 10px;
`;

const Button = styled.button`
  border: none;
  background-color: ${({ theme }) => theme.colors.lightgrey};
  color: ${({ theme }) => theme.colors.primary};
  font-size: 10px;
  height: 26px;
  width: 100%;
  border-radius: 4px;
  padding-inline: 10px;
  cursor: pointer;

  &.active {
    background-color: ${({ theme }) => theme.colors.red};
  }

  &:disabled {
    cursor: not-allowed;
  }
`;

const Input = styled.input`
  border: none;
  background-color: ${({ theme }) => theme.colors.lightgrey};
  color: ${({ theme }) => theme.colors.primary};
  font-size: 10px;
  width: 100%;
  height: 40px;
  border-radius: 4px;
  padding: 10px;
  margin-top: 10px;

  &.error {
    border: 1px solid ${({ theme }) => theme.colors.red};
  }
`;

const CustomDimensions = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
`;

// const SelectedTemplate = styled.div`
//   width: 100%;
//   padding: 4px;
//   border-radius: 4px;
//   background-color: ${({ theme }) => theme.colors.lightgrey};
//   margin-bottom: 10px;

//   & .inner {
//     display: flex;
//     align-items: center;
//     gap: 10px;

//     & img {
//       width: 30px;
//       height: 30px;
//       object-fit: cover;
//       border-radius: 4px;
//     }
//   }
// `;

const TextArea = styled.textarea`
  border: none;
  background-color: ${({ theme }) => theme.colors.lightgrey};
  color: ${({ theme }) => theme.colors.primary};
  font-size: 10px;
  width: 100%;
  border-radius: 4px;
  padding: 10px;
  margin-top: 10px;
  resize: none;
`;
